import React, { useState } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import { postData, getData, putData } from "../../../webService/webservice.js";
import StoreMultiSelect from "../../Challange/storeMultiSelect.jsx";
import TVFMLoader from "../Loader/loader.jsx";
import closeIcon from '@iconify/icons-mdi/close-circle-outline';
import "../../../styles/Auth/suppliers.css";
import { Typography } from "@mui/material";
import { useStore } from "../../../Context/context.jsx";

const AddSupplierForm = ({ onSuccess, onCancel, initialStoreId, isEditMode, editData }) => {
    const role = localStorage.getItem("role");
    const sessionToken = localStorage.getItem("Token");
    const { selectedStoreId: globalStoreId } = useStore();

    const [showLoader, setShowLoader] = useState(false);
    const [errors, setErrors] = useState({});
    const [selectedStores, setSelectedStores] = useState([]);

    const [formData, setFormData] = useState({
        storeId: [],
        storeName: '',
        supplierName: editData?.supplierName || '',
        contactPerson: editData?.contactPerson || '',
        email: editData?.email || '',
        phoneNumber: editData?.phoneNumber || '',
        address: editData?.address || '',
        city: editData?.city || '',
        state: editData?.state || '',
        country: editData?.country || '',
        pincode: editData?.pincode || '',
        gstNumber: editData?.gstNumber || ''
    });

    React.useEffect(() => {
        const initializeStores = async () => {
            const sid = isEditMode
                ? (editData?.storeId?._id || editData?.storeId)
                : (initialStoreId || globalStoreId);

            if (sid && sid !== "all") {
                try {
                    const res = await getData(`stores/store/${sid}`, {}, sessionToken);
                    if (res?.data) {
                        setFormData(prev => ({
                            ...prev,
                            storeId: [res.data._id],
                            storeName: res.data.storeName
                        }));
                        setSelectedStores([{ value: res.data._id, label: res.data.storeName }]);
                    }
                } catch (e) {
                    console.error("Error fetching store label", e);
                }
            }
        };
        initializeStores();
    }, [editData, initialStoreId, globalStoreId, sessionToken, isEditMode]);

    const EMPTY_ARRAY = [];

    const loadStores = async (inputValue, loadedOptions, { page }) => {
        try {
            const res = await getData(
                "stores/stores",
                { page, limit: 10, search: inputValue },
                sessionToken
            );

            const results = res?.data?.results || EMPTY_ARRAY;

            const options = results.map(store => ({
                value: store._id,
                label: store.storeName
            }));

            return {
                options,
                hasMore: results.length === 10,
                additional: { page: page + 1 }
            };

        } catch (err) {
            console.error("Store Fetch Failed", err);
            return { options: [], hasMore: false };
        }
    };

    const validateFields = (data) => {
        let newErrors = {};
        if (!data.storeId || data.storeId.length === 0) newErrors.storeId = "Store is required";
        if (!data.supplierName?.trim()) newErrors.supplierName = "Supplier Name is required";

        // Phone Number Validation (10 digits)
        if (!data.phoneNumber?.trim()) {
            newErrors.phoneNumber = "Phone Number is required";
        } else if (!/^\d{10}$/.test(data.phoneNumber.trim())) {
            newErrors.phoneNumber = "Invalid Phone Number (10 digits required)";
        }

        // Email Validation
        if (data.email?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim())) {
            newErrors.email = "Invalid email format";
        }

        // GST Number Validation (15 alphanumeric)
        if (data.gstNumber?.trim() && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(data.gstNumber.trim().toUpperCase())) {
            newErrors.gstNumber = "Invalid GST Number format";
        }

        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validateFields(formData);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setShowLoader(true);
        try {
            let response;
            if (isEditMode && editData?._id) {
                response = await putData(`suppliers/supplier/${editData._id}`, formData, sessionToken);
            } else {
                response = await postData("suppliers/", formData, sessionToken);
            }

            if (response.status === 200 || response.success) {
                toast.success(response.message || `Supplier ${isEditMode ? 'updated' : 'added'} successfully`);
                if (onSuccess) onSuccess(response.data);
            } else {
                toast.error(response.message || `Failed to ${isEditMode ? 'update' : 'add'} supplier`);
            }
        } catch (error) {
            toast.error(error.message || `${isEditMode ? 'Update' : 'Add'} Supplier Failed`);
        } finally {
            setTimeout(() => {
                setShowLoader(false);
            }, 1000);
        }
    };

    return (
        <div className="add-supplier-form">
            {showLoader && (
                <div className="scissor-overlay">
                    <div className="scissor-loader"><TVFMLoader /></div>
                </div>
            )}
            <Typography variant="h6" sx={{ mb: 2 }}>
                {isEditMode ? 'Edit Supplier' : 'Add Supplier'}
            </Typography>
            <form className="supplier-form" onSubmit={handleSubmit}>
                <div className="supplier-form-row">
                    <div className="supplier-form-group">
                        <label className="input-label">Supplier Name</label>
                        <input
                            type="text"
                            placeholder="Enter Supplier name"
                            className="supplier-input"
                            value={formData.supplierName}
                            onChange={e => setFormData(prev => ({ ...prev, supplierName: e.target.value }))}
                        />
                        {errors.supplierName && <p className="field-error">{errors.supplierName}</p>}
                    </div>

                    <div className="supplier-form-group">
                        <label className="input-label">Store Name</label>
                        <StoreMultiSelect
                            value={selectedStores}
                            onChange={(val) => {
                                setSelectedStores(val);
                                setFormData(prev => ({
                                    ...prev,
                                    storeId: val.map(v => v.value),
                                    storeName: val?.[0]?.label || ""
                                }));
                            }}
                            loadStores={loadStores}
                            loggedInRole={role}
                            assignedStoreIds={JSON.parse(localStorage.getItem("storeIds") || "[]")}
                        />
                        {errors.storeId && <p className="field-error">{errors.storeId}</p>}
                    </div>

                </div>

                <div className="supplier-form-row">
                    <div className="supplier-form-group">
                        <label className="input-label">Contact Person</label>
                        <input
                            type="text"
                            placeholder="Enter Contact Person"
                            className="supplier-input"
                            value={formData.contactPerson}
                            onChange={e => setFormData(prev => ({ ...prev, contactPerson: e.target.value }))}
                        />
                    </div>
                    <div className="supplier-form-group">
                        <label className="input-label">Email</label>
                        <input
                            type="email"
                            placeholder="Enter Email"
                            className="supplier-input"
                            value={formData.email}
                            onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        />
                        {errors.email && <p className="field-error">{errors.email}</p>}
                    </div>
                    <div className="supplier-form-group">
                        <label className="input-label">Phone Number</label>
                        <input
                            type="text"
                            placeholder="Enter Phone Number"
                            className="supplier-input"
                            value={formData.phoneNumber}
                            onChange={e => setFormData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                        />
                        {errors.phoneNumber && <p className="field-error">{errors.phoneNumber}</p>}
                    </div>
                </div>

                <div className="supplier-form-row">
                    <div className="supplier-form-group">
                        <label className="input-label">Address</label>
                        <textarea
                            placeholder="Enter Address"
                            className="supplier-input"
                            rows={3}
                            value={formData.address}
                            onChange={e => setFormData(prev => ({ ...prev, address: e.target.value }))}
                        />
                    </div>
                </div>

                <div className="supplier-form-row">
                    <div className="supplier-form-group">
                        <label className="input-label">City</label>
                        <input
                            type="text"
                            placeholder="Enter City"
                            className="supplier-input"
                            value={formData.city}
                            onChange={e => setFormData(prev => ({ ...prev, city: e.target.value }))}
                        />
                    </div>
                    <div className="supplier-form-group">
                        <label className="input-label">State</label>
                        <input
                            type="text"
                            placeholder="Enter State"
                            className="supplier-input"
                            value={formData.state}
                            onChange={e => setFormData(prev => ({ ...prev, state: e.target.value }))}
                        />
                    </div>
                    <div className="supplier-form-group">
                        <label className="input-label">Country</label>
                        <input
                            type="text"
                            placeholder="Enter Country"
                            className="supplier-input"
                            value={formData.country}
                            onChange={e => setFormData(prev => ({ ...prev, country: e.target.value }))}
                        />
                    </div>
                </div>

                <div className="supplier-form-row">
                    <div className="supplier-form-group">
                        <label className="input-label">Pincode</label>
                        <input
                            type="text"
                            placeholder="Enter Pincode"
                            className="supplier-input"
                            value={formData.pincode}
                            onChange={e => setFormData(prev => ({ ...prev, pincode: e.target.value }))}
                        />
                    </div>
                    <div className="supplier-form-group">
                        <label className="input-label">GST Number</label>
                        <input
                            type="text"
                            placeholder="Enter GST Number"
                            className="supplier-input"
                            value={formData.gstNumber}
                            onChange={e => setFormData(prev => ({ ...prev, gstNumber: e.target.value.toUpperCase() }))}
                        />
                        {errors.gstNumber && <p className="field-error">{errors.gstNumber}</p>}
                    </div>
                </div>

                <div className="supplier-buttons">
                    <button className="add-Product-button" type="submit">
                        {isEditMode ? 'Update Supplier' : 'Add Supplier'}
                    </button>
                    <button className="cancel-Product-button" type="button" onClick={onCancel}>Cancel</button>
                </div>
            </form>
        </div>
    );
};

export default AddSupplierForm;
